<script setup lang="ts">
/** Toasts (role="status"): 4.5 s, or 10 s with an action; paused on hover and focus. */
import { onBeforeUnmount, watch } from 'vue';
import { ui, type ToastItem } from '@/app/state';
import { dismissToast } from '@/app/announce';

const timers = new Map<number, { left: number; started: number; t: ReturnType<typeof setTimeout> | null }>();
function run(id: number) {
  const s = timers.get(id);
  if (!s) return;
  s.started = Date.now();
  s.t = setTimeout(() => close(id), s.left);
}
function pause(id: number) {
  const s = timers.get(id);
  if (!s || !s.t) return;
  clearTimeout(s.t);
  s.t = null;
  s.left = Math.max(1500, s.left - (Date.now() - s.started));
}
function close(id: number) {

  const el = document.querySelector(`[data-toast="${id}"]`);
  el?.classList.remove('in');
  setTimeout(() => { timers.delete(id); dismissToast(id); }, 400);
}
watch(() => ui.toasts.map((t) => t.id), (ids) => {
  for (const id of ids) {
    if (timers.has(id)) continue;
    const t = ui.toasts.find((x) => x.id === id)!;
    timers.set(id, { left: t.action ? 10000 : 4500, started: 0, t: null });
    run(id);
    requestAnimationFrame(() => requestAnimationFrame(() => document.querySelector(`[data-toast="${id}"]`)?.classList.add('in')));
  }
}, { immediate: true });
function act(t: ToastItem) { t.onAction?.(); close(t.id); }
onBeforeUnmount(() => { for (const s of timers.values()) if (s.t) clearTimeout(s.t); });
</script>

<template>
  <div id="toasts" class="toasts" role="status" aria-live="polite">
    <div v-for="t in ui.toasts" :key="t.id" class="toast" :class="t.tone" :data-toast="t.id" @pointerenter="pause(t.id)" @pointerleave="run(t.id)" @focusin="pause(t.id)" @focusout="run(t.id)">
      <span>{{ t.msg }}</span>
      <button v-if="t.action" type="button" class="toast-act" @click="act(t)">{{ t.action }}</button>
    </div>
  </div>
</template>
